import {
  getBudgetRanges,
  getBuildingCategories,
  getConstructionCategories,
  getConstructionTypes,
  getDeliveryMethods,
  getEstimateTypes,
  getPhaseTypes,
  getProcurementMethods,
} from '@services/api';
import type { ProjectType } from '@services/dto/projectShare';
import { useAppDispatch, useAppSelector } from '@services/state/store';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { projectSupportActions } from './slice';
import type { ProjectSupportState } from './types';

export type UseProjectSupportHook = [state: ProjectSupportState, refresh: () => void];

export const useProjectSupport = (): UseProjectSupportHook => {
  const state = useAppSelector((state) => state.projectSupport);
  const dispatch = useAppDispatch();

  const {
    i18n: { language },
  } = useTranslation();

  const refresh = React.useCallback(() => {
    dispatch(projectSupportActions.setLoading(true));

    Promise.all([
      getConstructionCategories(language),
      getConstructionTypes(language),
      getPhaseTypes(language),
      getProcurementMethods(language),
      getDeliveryMethods(language),
      getBudgetRanges(language),
      getBuildingCategories(language),
      getEstimateTypes(language),
    ])
      .then(
        ([
          constructionCategories,
          constructionTypes,
          phaseTypes,
          procurementMethods,
          deliveryMethods,
          budgetRanges,
          buildingCategories,
          estimateTypes,
        ]) => {
          // Flat project types list
          let projectTypes: ProjectType[] = [];
          buildingCategories.forEach((bc) => {
            projectTypes = [...projectTypes, ...bc.projectTypes];
          });
          projectTypes = projectTypes.sort((a, b) => {
            const ax = a.type.toUpperCase();
            const bx = b.type.toUpperCase();
            if (ax < bx) return -1;
            return ax > bx ? 1 : 0;
          });

          dispatch(
            projectSupportActions.setData({
              constructionCategories,
              constructionTypes,
              phaseTypes,
              procurementMethods,
              deliveryMethods,
              budgetRanges,
              buildingCategories,
              projectTypes,
              estimateTypes,
              language,
            }),
          );
        },
      )
      .catch((error) => {
        dispatch(projectSupportActions.setError(error));
      });
  }, [language]);

  React.useEffect(() => {
    if (state.language === language) return;
    refresh();
  }, [state.language, language, refresh]);

  return [state, refresh];
};
